import { InstrumentCategoryEnum, ListedInstrument, isListedInstrumentTradableInApp } from '@eih-netbroker/common';
import { MouseEvent, MouseEventHandler } from 'react';
import { ReactComponent as AddToChartIcon } from '../../../assets/icons/chart.svg';
import { ReactComponent as AddAlertIcon } from '../../../assets/icons/clock.svg';
import { ReactComponent as DetailsIcon } from '../../../assets/icons/docs.svg';
import { ReactComponent as SyncIcon } from '../../../assets/icons/exchange-horizontal.svg';
import { ReactComponent as AddToWatchlistIcon } from '../../../assets/icons/watchlistaddto.svg';
import { useLocale } from '../../../context/NbLocalizationContext';
import { useMarkets } from '../../services/useMarkets';
import { NbButton } from '../button/Button';
import { NbIconButton } from '../icon-button/IconButton';
import { InstrumentCategoryLogo } from '../instrument-category-logo/InstrumentCategoryLogo';
import { NbTooltip } from '../tooltip/Tooltip';
import classes from './InstrumentResultRow.module.scss';

export interface InstrumentResultRowProps {
  instrument: ListedInstrument;
  isWithActions?: boolean;
  isSelected?: boolean;
  onClick?: MouseEventHandler<HTMLDivElement>;
  onOrderClick?: (instrument: ListedInstrument) => void;
  onNotificationClick?: (instrument: ListedInstrument) => void;
  onChartClick?: (instrument: ListedInstrument) => void;
  onProductDetailsClick?: (instrument: ListedInstrument) => void;
  onWatchlistClick?: (e: MouseEvent<HTMLButtonElement>, instrument: ListedInstrument) => void;
}

export const InstrumentResultRow = ({
  instrument,
  isWithActions,
  isSelected,
  onOrderClick,
  onNotificationClick,
  onChartClick,
  onProductDetailsClick,
  onWatchlistClick,
  ...props
}: InstrumentResultRowProps) => {
  const handleOnOrderClick: MouseEventHandler<HTMLButtonElement> = e => {
    e.stopPropagation();
    onOrderClick?.(instrument);
  };

  const handlePriceAlert: MouseEventHandler<HTMLButtonElement> = e => {
    e.stopPropagation();
    onNotificationClick?.(instrument);
  };

  const handleChartClick: MouseEventHandler<HTMLButtonElement> = e => {
    e.stopPropagation();
    onChartClick?.(instrument);
  };

  const handleProductDetailsClick: MouseEventHandler<HTMLButtonElement> = e => {
    e.stopPropagation();
    onProductDetailsClick?.(instrument);
  };

  const handleWatchlistClick: MouseEventHandler<HTMLButtonElement> = e => {
    e.stopPropagation();
    onWatchlistClick?.(e, instrument);
  };

  const { translation } = useLocale();
  const { getMicName } = useMarkets();

  return (
    <div
      {...props}
      className={`${classes.item} ${isSelected ? classes.itemIsSelected : ''}`}
      data-is-selected={isSelected}
      role="button"
    >
      <div className={classes.itemContent}>
        <InstrumentCategoryLogo
          className={classes.itemLogo}
          instrumentCategory={instrument.instrumentCategory as InstrumentCategoryEnum}
        />
        <div className={classes.itemBody}>
          <div
            data-testid="instrument-search-item-name"
            className={classes.itemName}
          >
            {instrument.instrumentName}
          </div>
          <div
            data-testid="instrument-search-item-details"
            className={classes.itemDetails}
          >
            <span>{instrument.ticker || instrument.ric}</span>
            <span>{instrument.isin}</span>
            <span>{getMicName(instrument.mic)}</span>
            <span>{instrument.currency}</span>
          </div>
        </div>
      </div>

      {isWithActions && (
        <div className={classes.itemActions}>
          <NbTooltip
            title={
              !isListedInstrumentTradableInApp(instrument) ? translation.instrument.search.tooltip.nonTradeable : ''
            }
            placement={'top'}
          >
            <NbButton
              data-testid="instrument-search-order-button"
              onClick={handleOnOrderClick}
              disabled={!isListedInstrumentTradableInApp(instrument)}
              size={'small'}
              className={classes.itemActionOrderButton}
            >
              <SyncIcon /> {translation.instrument.search.order}
            </NbButton>
          </NbTooltip>
          <NbTooltip
            title={translation.instrument.search.tooltip.addToWatchlist}
            placement={'top'}
          >
            <span>
              <NbIconButton
                data-testid="instrument-search-watchlist-button"
                icon={<AddToWatchlistIcon />}
                onClick={handleWatchlistClick}
              />
            </span>
          </NbTooltip>
          <NbTooltip
            title={translation.instrument.search.tooltip.openChart}
            placement={'top'}
          >
            <span>
              <NbIconButton
                data-testid="instrument-search-chart-button"
                icon={<AddToChartIcon />}
                onClick={handleChartClick}
              />
            </span>
          </NbTooltip>
          <NbTooltip
            title={translation.instrument.search.tooltip.productPage}
            placement={'top'}
          >
            <span>
              <NbIconButton
                data-testid="instrument-search-product-details-button"
                icon={<DetailsIcon />}
                onClick={handleProductDetailsClick}
              />
            </span>
          </NbTooltip>
          <NbTooltip
            title={translation.instrument.search.tooltip.priceAlert}
            placement={'top'}
          >
            <span>
              <NbIconButton
                data-testid="instrument-search-price-alert-button"
                icon={<AddAlertIcon />}
                onClick={handlePriceAlert}
              />
            </span>
          </NbTooltip>
        </div>
      )}
    </div>
  );
};
